import {
  allowsCmrDeliveryNotePdf,
  allowsStandardDeliveryNotePdf,
  hasExternalQrCode,
  type DeliveryNote
} from '@/model/DeliveryNote'
import { deliveryNoteService } from '@/services/DeliveryNoteService'
import { ServiceError } from '@/services/util/ServiceError'
import { triggerBlobDownload } from '@/utils/blobDownload'
import { downloadExternalDeliveryNoteDocument } from '@/utils/externalDeliveryNoteDocument'
import type { MenuItem } from 'primevue/menuitem'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'

export function useDeliveryNoteDownload() {
  const toast = useToast()
  const { t } = useI18n()

  const showInvalidDocumentToast = () => {
    toast.add({
      severity: 'error',
      summary: t('deliveryNotes.messages.notifications.invalidDocument'),
      life: 3000
    })
  }

  const showDownloadErrorToast = () => {
    toast.add({
      severity: 'error',
      summary: `${t('deliveryNotes.messages.notifications.errorDownloadDeliveryNotePdf')} ${t('messages.notifications.tryLater')}`,
      life: 3000
    })
  }

  const showCmrDownloadErrorToast = () => {
    toast.add({
      severity: 'error',
      summary: `${t('deliveryNotes.messages.notifications.errorDownloadDeliveryNoteCmrPdf')} ${t('messages.notifications.tryLater')}`,
      life: 3000
    })
  }

  const downloadDeliveryNotePdfFile = (deliveryNote: DeliveryNote, response: Blob) => {
    const mimeType = 'application/pdf'
    const newBlob = new Blob([response], { type: mimeType })
    triggerBlobDownload(newBlob, `${deliveryNote.number}.pdf`)
  }

  const downloadCmrDeliveryNotePdfFile = (deliveryNote: DeliveryNote, response: Blob) => {
    const mimeType = 'application/pdf'
    const newBlob = new Blob([response], { type: mimeType })
    triggerBlobDownload(newBlob, `${deliveryNote.number}_cmr.pdf`)
  }

  const downloadExternalDeliveryNote = async (url: string, filename: string): Promise<void> => {
    const result = await downloadExternalDeliveryNoteDocument(url.trim(), filename)

    if (result === 'invalid') {
      showInvalidDocumentToast()
    } else if (result === 'error') {
      showDownloadErrorToast()
    }
  }

  const downloadCmrDeliveryNote = async (deliveryNote: DeliveryNote): Promise<void> => {
    const response = await deliveryNoteService.downloadCmrPdfById(deliveryNote.id)

    if (response instanceof ServiceError) {
      showCmrDownloadErrorToast()
    } else {
      downloadCmrDeliveryNotePdfFile(deliveryNote, response)
    }
  }

  const downloadDeliveryNote = async (deliveryNote: DeliveryNote): Promise<void> => {
    if (hasExternalQrCode(deliveryNote)) {
      await downloadExternalDeliveryNote(deliveryNote.externalQrCode!, deliveryNote.number)
      return
    }

    if (allowsCmrDeliveryNotePdf(deliveryNote) && !allowsStandardDeliveryNotePdf(deliveryNote)) {
      await downloadCmrDeliveryNote(deliveryNote)
      return
    }

    const response = await deliveryNoteService.downloadPdfById(deliveryNote.id)

    if (response instanceof ServiceError) {
      showDownloadErrorToast()
    } else {
      downloadDeliveryNotePdfFile(deliveryNote, response)
    }
  }

  const getDownloadLabel = (deliveryNote: DeliveryNote): string => {
    if (hasExternalQrCode(deliveryNote)) {
      return t('deliveryNotes.downloadDocument')
    }
    if (allowsCmrDeliveryNotePdf(deliveryNote) && !allowsStandardDeliveryNotePdf(deliveryNote)) {
      return t('deliveryNotes.downloadCmr')
    }
    return t('common.downloadPdf')
  }

  const buildDownloadMenuItem = (
    deliveryNote: DeliveryNote,
    onStandard: () => void,
    onCmr: () => void
  ): MenuItem => {
    if (hasExternalQrCode(deliveryNote)) {
      return {
        label: t('deliveryNotes.downloadDocument'),
        icon: 'pi pi-download',
        command: onStandard
      }
    }

    const showStandard = allowsStandardDeliveryNotePdf(deliveryNote)
    const showCmr = allowsCmrDeliveryNotePdf(deliveryNote)

    if (showStandard && showCmr) {
      return {
        label: t('common.download'),
        items: [
          {
            label: t('deliveryNotes.downloadDeliveryNote'),
            command: onStandard
          },
          {
            label: t('deliveryNotes.downloadCmr'),
            command: onCmr
          }
        ]
      }
    }

    if (showCmr) {
      return {
        label: t('deliveryNotes.downloadCmr'),
        icon: 'pi pi-download',
        command: onCmr
      }
    }

    return {
      label: t('deliveryNotes.downloadDeliveryNote'),
      icon: 'pi pi-download',
      command: onStandard
    }
  }

  return {
    downloadDeliveryNote,
    downloadCmrDeliveryNote,
    downloadDeliveryNotePdfFile,
    downloadCmrDeliveryNotePdfFile,
    downloadExternalDeliveryNote,
    getDownloadLabel,
    buildDownloadMenuItem,
    hasExternalQrCode
  }
}
